/**
 * Date utility functions for formatting and manipulating dates
 */

/**
 * Format ISO date string to a readable format
 * @param dateString - ISO date string (e.g., "2025-09-22T16:17:55.08")
 * @param format - Output format type
 * @returns Formatted date string
 */
export function formatDate(
  dateString: string | Date | null | undefined,
  format: 'short' | 'medium' | 'long' | 'time' | 'datetime' = 'medium'
): string {
  if (!dateString) {
    return '';
  }

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    const options: Intl.DateTimeFormatOptions = {};

    switch (format) {
      case 'short':
        options.day = '2-digit';
        options.month = '2-digit';
        options.year = '2-digit';
        break;
      case 'medium':
        options.day = '2-digit';
        options.month = 'short';
        options.year = 'numeric';
        break;
      case 'long':
        options.weekday = 'long';
        options.day = '2-digit';
        options.month = 'long';
        options.year = 'numeric';
        break;
      case 'time':
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.hour12 = true;
        break;
      case 'datetime':
        options.day = '2-digit';
        options.month = 'short';
        options.year = 'numeric';
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.hour12 = true;
        break;
    }

    return new Intl.DateTimeFormat('en-GB', options).format(date);
  } catch (error) {
    return '';
  }
}

/**
 * Format date to DD-MM-YYYY format (commonly used in the application)
 * @param dateString - ISO date string
 * @returns Formatted date string in DD-MM-YYYY format
 */
export function formatDateDDMMYYYY(
  dateString: string | Date | null | undefined
): string {
  if (!dateString) {
    return '';
  }

  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return '';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (error) {
    return '';
  }
}

/**
 * Format date to relative time (e.g., "2 hours ago", "3 days ago")
 * @param dateString - ISO date string
 * @returns Relative time string
 */
export function formatRelativeTime(
  dateString: string | Date | null | undefined
): string {
  if (!dateString) {
    return '';
  }

  try {
    const date = new Date(dateString);
    const now = new Date();

    if (isNaN(date.getTime())) {
      return '';
    }

    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return formatDate(dateString, 'medium');
    }
  } catch (error) {
    return '';
  }
}

/**
 * Check if a date is today
 * @param dateString - ISO date string
 * @returns True if the date is today
 */
export function isToday(dateString: string | Date | null | undefined): boolean {
  if (!dateString) {
    return false;
  }

  try {
    const date = new Date(dateString);
    const today = new Date();

    if (isNaN(date.getTime())) {
      return false;
    }

    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  } catch (error) {
    return false;
  }
}

/**
 * Get time ago string for display in UI
 * @param dateString - ISO date string
 * @returns Formatted time ago string
 */
export function getTimeAgo(
  dateString: string | Date | null | undefined
): string {
  if (!dateString) {
    return '';
  }

  try {
    const date = new Date(dateString);
    const now = new Date();

    if (isNaN(date.getTime())) {
      return '';
    }

    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 0) {
      return 'In the future';
    } else if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      return formatDate(dateString, 'short');
    }
  } catch (error) {
    return '';
  }
}
